import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useHybridAuthToken } from '../../components/hybridAuthToken';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Typography, FormControlLabel, FormControl, RadioGroup, Radio } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import axios from 'axios';
import useEnvConfig from '../../hooks/useEnvConfig';
import rollbar from '../../utils/rollbar';
import { BILLING_CONTACT_PATH, pathToRoute } from '../../constants/routes';
import { useHistory } from 'react-router-dom';
import DialogContentText from '@material-ui/core/DialogContentText';
import { usePaperBillFeeFlag } from '../../hooks/useGetFeatureFlag';

const BillingPreferences = ({ userInfo, refetchUserInfo }) => {
  const hybridAuth = useHybridAuthToken();
  const history = useHistory();
  const { userAPIUrl } = useEnvConfig();
  const { allowedStates } = usePaperBillFeeFlag();
  const {
    email,
    address,
    city,
    state,
    zip,
    paperBillFeeWaived,
    prospectId,
    contactId,
    serviceContractNumber,
    billingNotificationPreference,
  } = userInfo;

  const isNY = state?.toUpperCase() === 'NY';
  const showPaperBillFee =
    !isNY && allowedStates.some((s) => s.toUpperCase() === 'ALL' || s.toUpperCase() === state?.toUpperCase());

  const [isPaperBillFeeWaived, setIsPaperBillFeeWaived] = useState(paperBillFeeWaived || !showPaperBillFee);
  const [acceptTerms, setAcceptTerms] = useState(paperBillFeeWaived || !showPaperBillFee);
  const [billingPreference, setBillingPreference] = useState(
    billingNotificationPreference === 'Email' ? 'Email' : 'Paper',
  );
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Derived state - no need for useState or useEffect
  const hasWaiverChanged = isPaperBillFeeWaived !== paperBillFeeWaived && showPaperBillFee;
  const normalizedBillingNotificationPreference = billingNotificationPreference === 'Email' ? 'Email' : 'Paper';
  const hasBillingPrefChanged = billingPreference !== normalizedBillingNotificationPreference;
  const hasChanges = hasWaiverChanged || hasBillingPrefChanged;
  const isSubmitDisabled = !hasChanges || !acceptTerms || isLoading;

  const handlePaperBillFeeWaivedChange = () => {
    // Once paperBillFeeWaived is true, it cannot be changed
    if (paperBillFeeWaived) {
      return;
    }
    const newValue = !isPaperBillFeeWaived;
    setIsPaperBillFeeWaived(newValue);
    if (newValue) {
      setAcceptTerms(true);
    } else {
      setAcceptTerms(false);
    }
  };

  const handleAcceptTerms = () => {
    setAcceptTerms(!acceptTerms);
  };

  const handleBillingPreferenceChange = (event) => {
    setBillingPreference(event.target.value);
  };

  const submitBillingPreferences = async () => {
    let body;

    // CRM asks for contractId, but its actually serviceContractNumber they want
    if (isPaperBillFeeWaived && !paperBillFeeWaived && showPaperBillFee) {
      body = {
        paperBillFeeWaived: true,
        billingNotificationPreference: billingPreference,
        contractId: serviceContractNumber,
      };
    } else {
      body = {
        billingNotificationPreference: billingPreference,
        contractId: serviceContractNumber,
      };
    }
    try {
      setIsLoading(true);
      setHasError(false);
      const res = await axios.patch(`${userAPIUrl}/contract`, body, {
        headers: {
          Authorization: hybridAuth,
        },
      });

      setIsLoading(false);

      if (res.status === 200) {
        rollbar.info('Billing preferences submitted successfully');
        await refetchUserInfo();
        history.replace(pathToRoute(BILLING_CONTACT_PATH, { prospectId, contactId }));
      } else {
        rollbar.error(`Failed to submit billing preferences: ${res.status}`);
        setHasError(true);
      }
    } catch (error) {
      rollbar.error(`Failed to submit billing preferences: ${error}`);
      setIsLoading(false);
      setHasError(true);
    }
  };

  if (!hybridAuth) return <LinearProgress />;

  return (
    <div
      role="region"
      aria-live="assertive"
      data-testid="billing-preferences-container"
      style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
    >
      <Typography variant="h5" component="span">
        Billing Preferences
      </Typography>
      {showPaperBillFee && (
        <FormControlLabel
          control={
            <Checkbox
              name="paperBillFeeWaived"
              color="primary"
              onChange={handlePaperBillFeeWaivedChange}
              checked={isPaperBillFeeWaived}
              disabled={paperBillFeeWaived}
            />
          }
          label="Mark this customer as exempt to the Paper Billing Fee via the Americans with disabilities act"
          data-testid="exempt-to-paper-billing-fee"
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginTop: '20px' }}>
        <FormControl>
          <Typography variant="body1" component="span">
            Select one:
          </Typography>
          <RadioGroup
            value={billingPreference}
            name="billing-preference-group"
            onChange={handleBillingPreferenceChange}
            style={{ paddingTop: '20px', paddingLeft: '40px' }}
          >
            <FormControlLabel
              value="Email"
              control={<Radio name="send-email-bills-to" color="primary" />}
              label={`email to ${email} only`}
              data-testid="send-email-bills-to"
            />
            <FormControlLabel
              value="Paper"
              control={<Radio name="send-paper-bills-to" color="primary" />}
              label={`paper bill to ${address} ${city} ${state} ${zip}`}
              data-testid="send-paper-bills-to"
            />
          </RadioGroup>
        </FormControl>
        {!isPaperBillFeeWaived && (
          <>
            <FormControlLabel
              control={
                <Checkbox name="acceptTerms" color="primary" onChange={handleAcceptTerms} checked={acceptTerms} />
              }
              label="Read to customer, then check the box if they agree:"
              data-testid="acceptTerms"
            />
            {billingPreference === 'Paper' ? (
              <Typography variant="body2" component="span" style={{ color: '#616578' }}>
                By requesting receiving paper bills in the mail, you understand a $2.99 per month Paper Billing Fee will
                be applied to your future Sunrun bills. You can also update your billing preferences in the Sunrun
                mobile app. When adding or removing the paper billing, please note that the change will not take effect
                until the next bill.
              </Typography>
            ) : (
              <Typography variant="body2" component="span" style={{ color: '#616578' }}>
                By going paperless, you will only receive bills digitally via email to {email}. Your $2.99 per month
                Paper Billing Fee will be removed, but please note that the change will not take effect until the next
                bill. You can also update your billing preferences in the Sunrun mobile app.
              </Typography>
            )}
          </>
        )}

        <Button
          variant="contained"
          onClick={submitBillingPreferences}
          disabled={isSubmitDisabled}
          data-testid="billing-preferences-save-btn"
          style={{
            width: '194px',
            borderRadius: '30px',
            color: '#FFFFFF',
            backgroundColor: isSubmitDisabled ? '#B6B8C0' : '#1F2647',
          }}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Save changes'}
        </Button>
        {hasError && <DialogContentText color="error">Something went wrong. Try again later.</DialogContentText>}
      </div>
    </div>
  );
};

BillingPreferences.propTypes = {
  userInfo: PropTypes.shape({
    prospectId: PropTypes.string.isRequired,
    contactId: PropTypes.string.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string.isRequired,
    mobilePhoneNumber: PropTypes.string,
    phoneNumber: PropTypes.string,
    address: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    lastDueDate: PropTypes.string,
    listOfDueDates: PropTypes.string,
    lastDueDateUpdate: PropTypes.string,
    purchasedThru: PropTypes.string,
    totalAmountDue: PropTypes.string,
    paperBillFeeWaived: PropTypes.bool,
    billingNotificationPreference: PropTypes.string,
    serviceContractNumber: PropTypes.string.isRequired,
  }),
  refetchUserInfo: PropTypes.func.isRequired,
  specialInvoiceAmount: PropTypes.number,
  referenceNumberPrefix: PropTypes.string,
  isBillingEscalations: PropTypes.bool,
  setCurrentLastDueDateUpdate: PropTypes.func,
};

export default BillingPreferences;
